// src/components/Modals/MasterDataDeleteModal.js
import React, { useEffect, useCallback } from 'react';
import styles from './MasterDataDeleteModal.module.css';

const MasterDataDeleteModal = ({ itemName, onClose, onConfirm, loading }) => {
  
  // Prevent background scrolling when modal is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget && !loading) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  }, [loading, onClose]);

  const handleModalClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handleClose = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!loading) {
      onClose();
    }
  }, [loading, onClose]);

  const handleConfirm = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!loading) {
      onConfirm();
    }
  }, [loading, onConfirm]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !loading) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [loading, onClose]);

  return (
    <div 
      className={styles.modalOverlay} 
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="master-delete-modal-title"
      data-modal="master-delete-confirmation"
    >
      <div className={styles.modalContent} onClick={handleModalClick}>
        <div className={styles.modalHeader}>
          <h3 id="master-delete-modal-title">
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
            Confirm Master Data Deletion
          </h3>
          <button 
            type="button"
            className={styles.closeBtn} 
            onClick={handleClose} 
            disabled={loading}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.warningIcon}>
            <i className="fas fa-database" aria-hidden="true"></i>
          </div>
          <p className={styles.confirmText}>
            Are you sure you want to delete <strong>{itemName}</strong>?
          </p>
          <p className={styles.warningText}>
            <i className="fas fa-info-circle" aria-hidden="true"></i>
            This master data entry will be permanently removed and cannot be undone.
          </p>
          <p className={styles.impactText}>
            This may affect other records that reference this data.
          </p>
        </div>
        <div className={styles.modalFooter}>
          <button 
            type="button"
            className={`${styles.btn} ${styles.btnSecondary}`} 
            onClick={handleClose}
            disabled={loading}
          >
            <i className="fas fa-times" aria-hidden="true"></i>
            Cancel
          </button>
          <button 
            type="button"
            className={`${styles.btn} ${styles.btnDanger}`} 
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className={styles.spinnerBorder}></span>
                Deleting...
              </>
            ) : (
              <>
                <i className="fas fa-trash" aria-hidden="true"></i>
                Delete Permanently
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MasterDataDeleteModal;
